import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Tag, Calendar, Percent, Gift, X, Save, Eye, EyeOff, DollarSign, Users, Copy, Check, QrCode, Share2, Printer, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function MinhasPromocoes({ business }) {
  const { token } = useAuth();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [qrPromo, setQrPromo] = useState(null);
  const qrRef = useRef(null);
  const [formData, setFormData] = useState({
    titulo: '', descricao: '', codigo: '',
    tipo_desconto: 'percentual', valor_desconto: '',
    valor_minimo: '', max_usos: '', max_usos_por_usuario: '1',
    data_inicio: '', data_fim: '', termos: '', ativo: true,
  });

  useEffect(() => {
    if (business?.id) loadPromotions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  // Auto-open create modal when arriving via CTA link
  // (`/perfil?tab=negocios&new=promo&negocio_id=<id>`).
  useEffect(() => {
    if (!business?.id) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('new') === 'promo' && sp.get('negocio_id') === business.id) {
      setEditingPromo(null);
      setFormData({ titulo: '', descricao: '', codigo: '', tipo_desconto: 'percentual', valor_desconto: '', valor_minimo: '', max_usos: '', max_usos_por_usuario: '1', data_inicio: '', data_fim: '', termos: '', ativo: true });
      setShowModal(true);
      // Consome os params para não reabrir em refresh
      sp.delete('new'); sp.delete('negocio_id');
      const qs = sp.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    }
  }, [business?.id]);

  const loadPromotions = async () => {
    try {
      const res = await api.get(`/v2/coupons/business/${business.id}?ativo=`);
      setPromotions(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar cupons:', e);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        business_id: business.id,
        titulo: formData.titulo,
        descricao: formData.descricao,
        codigo: formData.codigo.toUpperCase(),
        tipo_desconto: formData.tipo_desconto,
        valor_desconto: parseFloat(formData.valor_desconto) || 0,
        valor_minimo: parseFloat(formData.valor_minimo) || 0,
        max_usos: parseInt(formData.max_usos) || 0,
        max_usos_por_usuario: parseInt(formData.max_usos_por_usuario) || 1,
        data_inicio: formData.data_inicio || null,
        data_fim: formData.data_fim || null,
        termos: formData.termos,
      };
      if (editingPromo) {
        await api.put(`/v2/coupons/${editingPromo.id}`, payload);
        toast.success('Cupom atualizado!');
      } else {
        await api.post('/v2/coupons', payload);
        toast.success('Cupom criado!');
      }
      loadPromotions();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cupom');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir este cupom?')) return;
    try {
      await api.delete(`/promotions/${id}`);
      toast.success('Cupom excluido');
      loadPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/promotions/${id}/toggle`);
      loadPromotions();
    } catch { toast.error('Erro'); }
  };

  const openModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setFormData({
        titulo: promo.titulo || '', descricao: promo.descricao || '',
        codigo: promo.codigo || '', tipo_desconto: promo.tipo_desconto || 'percentual',
        valor_desconto: promo.valor_desconto || '', valor_minimo: promo.valor_minimo || '',
        max_usos: promo.max_usos || '', max_usos_por_usuario: promo.max_usos_por_usuario || '1',
        data_inicio: promo.data_inicio?.split('T')[0] || '', data_fim: promo.data_fim?.split('T')[0] || '',
        termos: promo.termos || '', ativo: promo.ativo !== false,
      });
    } else {
      setEditingPromo(null);
      setFormData({ titulo: '', descricao: '', codigo: '', tipo_desconto: 'percentual', valor_desconto: '', valor_minimo: '', max_usos: '', max_usos_por_usuario: '1', data_inicio: '', data_fim: '', termos: '', ativo: true });
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingPromo(null); };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    setFormData({ ...formData, codigo: code });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const isExpired = (d) => d && new Date(d) < new Date();

  const getCouponUrl = (promo) => {
    const base = window.location.origin;
    return `${base}/cupom/${promo.codigo}`;
  };

  const handleShareQR = async (promo) => {
    const url = getCouponUrl(promo);
    const text = `${promo.titulo} - Use o codigo ${promo.codigo} e ganhe ${formatDiscount(promo)} de desconto!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: promo.titulo, text, url });
      } catch {}
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success('Link copiado!');
    }
  };

  const handlePrintQR = () => {
    const el = qrRef.current;
    if (!el) return;
    const win = window.open('', '_blank', 'width=400,height=600');
    win.document.write(`<html><head><title>QR Code - ${qrPromo.titulo}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:40px}
      .code{font-size:32px;font-weight:bold;letter-spacing:4px;margin:16px 0;padding:12px;border:3px dashed #d97706;border-radius:12px;display:inline-block}
      .title{font-size:20px;font-weight:bold;margin-bottom:8px}
      .discount{font-size:28px;color:#d97706;font-weight:bold}
      .desc{color:#666;margin:8px 0}</style></head><body>`);
    win.document.write(`<p class="title">${qrPromo.titulo}</p>`);
    win.document.write(`<p class="discount">${formatDiscount(qrPromo)} OFF</p>`);
    if (qrPromo.descricao) win.document.write(`<p class="desc">${qrPromo.descricao}</p>`);
    win.document.write(el.querySelector('svg').outerHTML);
    win.document.write(`<p class="code">${qrPromo.codigo}</p>`);
    if (qrPromo.data_fim) win.document.write(`<p class="desc">Valido ate ${new Date(qrPromo.data_fim).toLocaleDateString('pt-BR')}</p>`);
    win.document.write(`<p class="desc" style="margin-top:24px;font-size:12px;color:#999">${business?.nome || ''}</p>`);
    win.document.write('</body></html>');
    win.document.close();
    win.print();
  };

  const handleDownloadQR = () => {
    const el = qrRef.current;
    if (!el) return;
    const svg = el.querySelector('svg');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 512; canvas.height = 512;
    const img = new Image();
    const svgData = new XMLSerializer().serializeToString(svg);
    img.onload = () => {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      const a = document.createElement('a');
      a.download = `cupom-${qrPromo.codigo}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const formatDiscount = (p) => {
    if (p.tipo_desconto === 'percentual') return `${p.valor_desconto || 0}%`;
    if (p.tipo_desconto === 'fixo') return `R$ ${(p.valor_desconto || 0).toFixed(2)}`;
    return 'Brinde';
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-[var(--primary)] border-t-transparent rounded-full"></div></div>;

  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-[var(--primary)]" />
          <h3 className="font-bold text-lg">Cupons e Ofertas</h3>
          {promotions.length > 0 && <span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">{promotions.length}</span>}
        </div>
        <button onClick={() => openModal()} className="flex items-center gap-2 bg-[var(--primary)] text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-[var(--primary)]/90 transition-all" data-testid="add-coupon-btn">
          <Plus className="w-4 h-4" /> Novo Cupom
        </button>
      </div>

      {promotions.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-xl">
          <Gift className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-[var(--text-secondary)] text-sm">Nenhum cupom cadastrado</p>
          <p className="text-xs text-gray-400 mt-1">Crie cupons de desconto para atrair mais clientes!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promotions.map(promo => (
            <div key={promo.id} className={`border rounded-xl p-4 ${!promo.ativo || isExpired(promo.data_fim) ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
              <div className="flex items-start gap-3">
                {/* Discount badge */}
                <div className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-lg p-2.5 text-center min-w-[70px]">
                  {promo.tipo_desconto === 'percentual' ? <Percent className="w-4 h-4 mx-auto text-amber-600" /> : promo.tipo_desconto === 'fixo' ? <DollarSign className="w-4 h-4 mx-auto text-amber-600" /> : <Gift className="w-4 h-4 mx-auto text-amber-600" />}
                  <p className="font-bold text-amber-800 text-sm">{formatDiscount(promo)}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-sm truncate">{promo.titulo}</h4>
                    {promo.esgotado && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded-full">Esgotado</span>}
                  </div>
                  {promo.descricao && <p className="text-xs text-[var(--text-secondary)] line-clamp-1 mb-1.5">{promo.descricao}</p>}
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <button onClick={() => copyCode(promo.codigo)} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded font-mono hover:bg-gray-200 transition-colors">
                      {copiedCode === promo.codigo ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                      {promo.codigo}
                    </button>
                    {(promo.total_usos > 0 || promo.max_usos > 0) && (
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{promo.total_usos || 0}{promo.max_usos > 0 ? `/${promo.max_usos}` : ''} usos</span>
                    )}
                    {promo.data_fim && (
                      <span className={`flex items-center gap-1 ${isExpired(promo.data_fim) ? 'text-red-500' : ''}`}>
                        <Calendar className="w-3 h-3" />{isExpired(promo.data_fim) ? 'Expirado' : `Ate ${new Date(promo.data_fim).toLocaleDateString('pt-BR')}`}
                      </span>
                    )}
                    {!promo.ativo && <span className="flex items-center gap-1 text-gray-400"><EyeOff className="w-3 h-3" />Inativo</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setQrPromo(promo)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg" title="QR Code"><QrCode className="w-4 h-4" /></button>
                  <button onClick={() => handleToggle(promo.id)} className="p-1.5 hover:bg-gray-100 rounded-lg" title={promo.ativo ? 'Desativar' : 'Ativar'}>
                    {promo.ativo ? <Eye className="w-4 h-4 text-green-500" /> : <EyeOff className="w-4 h-4 text-gray-400" />}
                  </button>
                  <button onClick={() => openModal(promo)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(promo.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Code Modal */}
      {qrPromo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setQrPromo(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2"><QrCode className="w-5 h-5 text-amber-600" />QR Code do Cupom</h2>
              <button onClick={() => setQrPromo(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 flex flex-col items-center" ref={qrRef}>
              <div className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-xl p-4 mb-4 text-center">
                <p className="font-bold text-lg text-amber-800">{formatDiscount(qrPromo)} OFF</p>
                <p className="text-sm text-amber-700">{qrPromo.titulo}</p>
              </div>
              <QRCodeSVG
                value={getCouponUrl(qrPromo)}
                size={200}
                level="M"
                includeMargin
                bgColor="#ffffff"
                fgColor="#1e293b"
              />
              <div className="mt-4 bg-gray-100 rounded-xl px-6 py-3 text-center">
                <p className="text-xs text-gray-500 mb-1">Codigo</p>
                <p className="font-mono font-bold text-xl tracking-widest text-gray-800">{qrPromo.codigo}</p>
              </div>
              {qrPromo.data_fim && (
                <p className="text-xs text-gray-400 mt-2">Valido ate {new Date(qrPromo.data_fim).toLocaleDateString('pt-BR')}</p>
              )}
            </div>
            <div className="p-4 border-t flex gap-2">
              <button onClick={() => handleShareQR(qrPromo)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700">
                <Share2 className="w-4 h-4" /> Compartilhar
              </button>
              <button onClick={handlePrintQR} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-800 text-white rounded-xl text-sm font-medium hover:bg-gray-900">
                <Printer className="w-4 h-4" /> Imprimir
              </button>
              <button onClick={handleDownloadQR} className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold">{editingPromo ? 'Editar Cupom' : 'Novo Cupom'}</h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titulo *</label>
                <input type="text" required value={formData.titulo} onChange={e => setFormData({ ...formData, titulo: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" placeholder="Ex: 20% de desconto" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descricao</label>
                <textarea value={formData.descricao} onChange={e => setFormData({ ...formData, descricao: e.target.value })}
                  rows={2} className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" placeholder="Detalhes do cupom..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de Desconto</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'percentual', label: 'Percentual', icon: Percent },
                    { value: 'fixo', label: 'Valor Fixo', icon: DollarSign },
                    { value: 'brinde', label: 'Brinde', icon: Gift },
                  ].map(t => {
                    const Icon = t.icon;
                    return (
                      <button key={t.value} type="button" onClick={() => setFormData({ ...formData, tipo_desconto: t.value })}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${formData.tipo_desconto === t.value ? 'border-[var(--primary)] bg-[var(--primary)]/5' : 'border-[var(--border)]'}`}>
                        <Icon className="w-5 h-5 text-[var(--primary)]" /><span className="text-xs font-medium">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {formData.tipo_desconto !== 'brinde' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">{formData.tipo_desconto === 'percentual' ? 'Desconto (%)' : 'Valor (R$)'}</label>
                    <input type="number" min="0" step={formData.tipo_desconto === 'percentual' ? '1' : '0.01'} max={formData.tipo_desconto === 'percentual' ? '100' : undefined}
                      value={formData.valor_desconto} onChange={e => setFormData({ ...formData, valor_desconto: e.target.value })}
                      className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" placeholder={formData.tipo_desconto === 'percentual' ? '20' : '10.00'} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Compra Minima (R$)</label>
                    <input type="number" min="0" step="0.01" value={formData.valor_minimo} onChange={e => setFormData({ ...formData, valor_minimo: e.target.value })}
                      className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" placeholder="0 = sem minimo" />
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Codigo do Cupom *</label>
                <div className="flex gap-2">
                  <input type="text" required value={formData.codigo} onChange={e => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                    className="flex-1 px-4 py-2.5 border border-[var(--border)] rounded-xl font-mono uppercase" placeholder="DESCONTO20" />
                  <button type="button" onClick={generateCode} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm font-medium">Gerar</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Limite de Usos</label>
                  <input type="number" min="0" value={formData.max_usos} onChange={e => setFormData({ ...formData, max_usos: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" placeholder="0 = ilimitado" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Usos por Usuario</label>
                  <input type="number" min="0" value={formData.max_usos_por_usuario} onChange={e => setFormData({ ...formData, max_usos_por_usuario: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" placeholder="1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Inicio</label>
                  <input type="date" value={formData.data_inicio} onChange={e => setFormData({ ...formData, data_inicio: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Validade</label>
                  <input type="date" value={formData.data_fim} onChange={e => setFormData({ ...formData, data_fim: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Termos e Condicoes</label>
                <input type="text" value={formData.termos} onChange={e => setFormData({ ...formData, termos: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--border)] rounded-xl" placeholder="Valido apenas para primeira compra..." />
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <button type="button" onClick={closeModal} className="flex-1 px-4 py-2.5 border border-[var(--border)] rounded-xl hover:bg-gray-50">Cancelar</button>
                <button type="submit" className="flex-1 px-4 py-2.5 bg-[var(--primary)] text-white rounded-xl hover:bg-[var(--primary)]/90 flex items-center justify-center gap-2">
                  <Save className="w-4 h-4" />{editingPromo ? 'Salvar' : 'Criar Cupom'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
